import { defineAsyncComponent } from 'vue';

import type { TikTokMediaType } from '@/apis/queries/media-list.types';
import type { SourceFilterConfig } from '@/app/competitive/source-filters';
import type {
  CompetitiveOverviewConfig,
  CompetitiveBenchmarkQueryInput,
  MostEngagingPostsFilterInput,
  MostEngagingPostsSeeMoreInput,
} from '@/app/competitive/useCompetitiveChannelConfig';

import {
  ADD_COMPETITOR_DATA_LOADING_MESSAGE,
  ADD_COMPETITOR_DATA_LOADING_MESSAGE_SINGULAR,
  ADD_COMPETITOR_INFO_MESSAGE,
} from '@/app/competitive/add-competitor/constants';
import { COMPETITOR_PLATFORM_OPTIONS } from '@/app/settings/constants';
import MediaCard from '@/components/dashingUI/MediaCard.vue';
import { EVENT } from '@/stores/socket';
import { toUtcQueryDateTime } from '@/utils';

export const TIKTOK_FILTERS = Object.freeze({
  FILTER_POST_TYPES: 'competitive_post_types',
});

export const TIKTOK_COMPETITOR_POSTS_ROUTE_NAME = 'competitors.tiktok.competitorPosts';

export const COMPETITOR_RESOLUTION_MAX_WAIT_MS = 120_000;

export const TIKTOK_MEDIA_TYPES = Object.freeze([
  { label: 'Photo', value: 'photo' },
  { label: 'Video', value: 'video' },
]);

export const TIKTOK_POST_METRICS = Object.freeze([
  {
    value: 'AVG_ENGAGEMENT_RATE_VIEWS',
    label: 'Avg. Engagement Rate (Views)',
    tooltip:
      'The estimated average percentage of people who engaged with your posts after seeing them, calculated using video views',
    percent: true,
    default: true,
  },
  {
    value: 'AVG_ENGAGEMENT_RATE_FOLLOWERS',
    label: 'Avg. Engagement Rate (Followers)',
    tooltip:
      'The estimated average percentage of people who engaged with your posts, calculated using followers',
    percent: true,
  },
  {
    value: 'TOTAL_ENGAGEMENTS',
    label: 'Engagements',
    tooltip:
      'The estimated total number of engagements your posts received. Engagements = Likes + Comments + Shares + Favorites.',
    supportsShareOfVoice: true,
  },
  {
    value: 'VIDEO_VIEWS',
    label: 'Video Views',
    tooltip: 'The estimated number of times your videos were viewed',
    supportsShareOfVoice: true,
  },
  {
    value: 'AVG_VIDEO_VIEWS',
    label: 'Avg. Video Views',
    tooltip: 'The estimated average number of times your videos were viewed',
  },
  {
    value: 'NUMBER_OF_POSTS',
    label: 'Number of Posts',
    tooltip: 'The estimated number of posts published during the selected period',
    supportsShareOfVoice: true,
  },
  {
    value: 'LIKES',
    label: 'Likes',
    tooltip: 'The estimated number of likes your posts received',
    supportsShareOfVoice: true,
  },
  {
    value: 'COMMENTS',
    label: 'Comments',
    tooltip: 'The estimated number of comments your posts received',
    supportsShareOfVoice: true,
  },
  {
    value: 'SHARES',
    label: 'Shares',
    tooltip: 'The estimated number of times your posts were shared',
    supportsShareOfVoice: true,
  },
  {
    value: 'FAVORITES',
    label: 'Favorites',
    tooltip: 'The estimated total number of times your posts were added to favorites',
    supportsShareOfVoice: true,
  },
]);

export const TIKTOK_ACCOUNT_METRICS = Object.freeze([
  {
    value: 'TOTAL_FOLLOWERS',
    label: 'Followers',
    tooltip: 'The estimated number of followers on your account',
    supportsShareOfVoice: true,
  },
]);

export const TIKTOK_METRICS = Object.freeze([...TIKTOK_POST_METRICS, ...TIKTOK_ACCOUNT_METRICS]);

export const TIKTOK_POST_SORT_OPTIONS = Object.freeze([
  { value: 'DATE', label: 'Recently Added' },
  { value: 'TIKTOK_LIKES', label: 'Likes', field: 'likes' },
  { value: 'TIKTOK_COMMENTS', label: 'Comments', field: 'comments' },
  { value: 'TIKTOK_SHARES', label: 'Shares', field: 'shares' },
  { value: 'TIKTOK_FAVORITES', label: 'Favorites', field: 'favorites' },
  { value: 'TIKTOK_VIEWS', label: 'Video Views', field: 'views' },
  { value: 'TIKTOK_TOTAL_ENGAGEMENTS', label: 'Engagements', field: 'totalEngagements' },
  { value: 'TIKTOK_EMV', label: 'EMV', field: 'emv' },
  {
    value: 'TIKTOK_VIEWS_BASED_ENGAGEMENT_RATE',
    label: 'Engagement Rate (Views)',
    field: 'viewsBasedEngagementRate',
  },
  {
    value: 'TIKTOK_FOLLOWERS_BASED_ENGAGEMENT_RATE',
    label: 'Engagement Rate (Followers)',
    field: 'followersBasedEngagementRate',
  },
]);

export const TIKTOK_TOP_PERFORMING_MEDIA_METRICS = Object.freeze({
  labels: {
    viewsBasedEngagementRate: 'Engagement Rate (Views)',
    likes: 'Likes',
    comments: 'Comments',
    shares: 'Shares',
  },
  metrics: ['viewsBasedEngagementRate', 'likes', 'comments', 'shares'],
});

// TODO[SC-210798]: Wire Overview defaults into shared benchmark graph filter state.
export const TIKTOK_FILTER_DEFAULTS = Object.freeze({
  overview: {
    reportingPeriod: { preset: 'LAST_7_DAYS' },
    competitors: [],
  },
  posts: {
    reportingPeriod: { preset: 'ALL_TIME' },
    captionContain: [],
    captionNotContain: [],
    competitors: [],
    hashtags: [],
    mediaType: [],
    contentTags: [],
    contentTagMatchType: 'ANY',
  },
});

const ADD_COMPETITOR_NOTIFICATIONS = Object.freeze({
  pendingDelay: 8000,
  pendingMessage: 'Adding competitors',
  pendingSubtext: ADD_COMPETITOR_DATA_LOADING_MESSAGE,
  pendingSingularSubtext: ADD_COMPETITOR_DATA_LOADING_MESSAGE_SINGULAR,
  requestError: "We couldn't add these competitors. Try again.",
  refreshError:
    "Competitors were added, but the page couldn't refresh. Refresh the page to see the latest data.",
  successDelay: 8000,
});

export const TIKTOK_ADD_COMPETITOR_CONFIG = Object.freeze({
  atCapacityMessage: COMPETITOR_PLATFORM_OPTIONS.TIKTOK.limitReachedMessage,
  infoMessage: ADD_COMPETITOR_INFO_MESSAGE,
  maxBulkHandles: 50,
  notifications: ADD_COMPETITOR_NOTIFICATIONS,
  placeholder: 'Enter full TikTok handle',
});

export const TIKTOK_CUSTOMER_COPY = Object.freeze({
  platformName: 'TikTok',
  pendingCompetitorTooltip:
    "We're still resolving this handle. It can take up to 24 hours to verify.",
  inaccessibleCompetitorTooltip:
    "This TikTok Competitor's handle has changed, is private, or is no longer valid. Remove this competitor and try a different handle.",
  noBrandPermission: "Looks like this feature isn't included in your plan!",
  noUserPermission: "Looks like you don't have permission to use this feature!",
  noMediaForDatesEmptyMessages: 'No posts found during this time period',
  noMediaForFiltersEmptyMessages: 'No posts match your selected filters',
  mostEngagingPostsTooltip:
    'Highest performing TikTok competitor posts published during the selected date range by engagement rate',
  mostEngagingPostsEmptyMessage: 'No Competitor Posts',
  mostEngagingPostsReportTitle: 'Top Performing Posts',
  shareofVoice:
    'Share is the measure of your specific brand metric divided by the total of the competitors selected',
  noCompetitorsEmptyStatus: {
    title: 'Add Competitors to Get Started',
  },
  noFilteredCompetitorsEmptyStatus: {
    message: 'There are no matching competitors for this set of filters. Remove some to try again.',
    title: 'Remove Filters',
  },
});

export const COMPETITIVE_OVERVIEW_CONFIG = Object.freeze({
  filter: {
    default: TIKTOK_FILTER_DEFAULTS.overview,
    queryParamMapping: {
      'reportingPeriod.range.start': { queryParamName: 'start_date', type: 'date' },
      'reportingPeriod.range.end': { queryParamName: 'end_date', type: 'date' },
      competitors: { queryParamName: 'competitors', type: 'array', elementType: 'string' },
    },
  },
  benchmark: {
    query: {
      build: ({
        brandId,
        endDate,
        metric,
        sourceAccountIds,
        startDate,
      }: CompetitiveBenchmarkQueryInput) => {
        const start = toUtcQueryDateTime(startDate);
        const end = toUtcQueryDateTime(endDate);
        return {
          brandIds: [brandId],
          competitorSourceAccountIds: [...new Set(sourceAccountIds)].toSorted(),
          channels: 'TIKTOK_COMPETITIVE',
          metrics: metric,
          startDate: start,
          endDate: end,
          contextStartDate: start,
          contextEndDate: end,
          reportType: 'BAR_METRIC',
          aggregateBy: 'BRAND',
          includeShareOfVoice: true,
        };
      },
    },
    channelKey: 'TIKTOK_COMPETITIVE',
    inaccessibleTooltip: TIKTOK_CUSTOMER_COPY.inaccessibleCompetitorTooltip,
    metrics: TIKTOK_METRICS,
    pendingTooltip: TIKTOK_CUSTOMER_COPY.pendingCompetitorTooltip,
  },
  mostEngagingPosts: {
    card: {
      component: MediaCard,
      props: {
        displayMetrics: TIKTOK_TOP_PERFORMING_MEDIA_METRICS.metrics,
        metricLabels: TIKTOK_TOP_PERFORMING_MEDIA_METRICS.labels,
        hideSentiment: true,
        showUserDetails: true,
      },
    },
    copy: {
      emptyMessage: TIKTOK_CUSTOMER_COPY.mostEngagingPostsEmptyMessage,
      reportTitle: TIKTOK_CUSTOMER_COPY.mostEngagingPostsReportTitle,
      tooltip: TIKTOK_CUSTOMER_COPY.mostEngagingPostsTooltip,
    },
    seeMore: {
      buildRoute: ({
        endDate,
        selectedSourceAccountIds,
        startDate,
      }: MostEngagingPostsSeeMoreInput) => ({
        name: TIKTOK_COMPETITOR_POSTS_ROUTE_NAME,
        query: {
          ...(selectedSourceAccountIds.length
            ? { competitors: [...new Set(selectedSourceAccountIds)].toSorted() }
            : {}),
          endDate,
          sort: '-TIKTOK_VIEWS_BASED_ENGAGEMENT_RATE',
          startDate,
        },
      }),
      label: 'See More',
    },
    query: {
      buildFilters: ({ endDate, sourceAccountIds, startDate }: MostEngagingPostsFilterInput) => ({
        brandMediaTypes: ['TIKTOK_OTHER'],
        sourceAccountIds: [...new Set(sourceAccountIds)].toSorted(),
        sourceCreatedAt: { start: startDate, end: endDate },
      }),
      limit: 5,
      sort: '-TIKTOK_ENGAGEMENT_RATE',
    },
  },
  handleResolutionError: {
    component: defineAsyncComponent(
      () => import('@/app/competitive/components/CompetitorResolutionBanner.vue'),
    ),
    props: {
      maxWaitMs: COMPETITOR_RESOLUTION_MAX_WAIT_MS,
      socketEvent: EVENT.COMPETITOR_RESOLUTION_UPDATED,
      source: COMPETITOR_PLATFORM_OPTIONS.TIKTOK.value,
      sourceLabel: TIKTOK_CUSTOMER_COPY.platformName,
    },
  },
}) satisfies CompetitiveOverviewConfig;

function isTikTokMediaType(value: string): value is TikTokMediaType {
  return value === 'PHOTO' || value === 'VIDEO';
}

export const TIKTOK_SOURCE_FILTER_CONFIG: SourceFilterConfig = {
  platformKey: 'tiktok',
  mediaTypeKey: 'mediaType',
  mapMediaTypes: (values) => values.map((value) => value.toUpperCase()).filter(isTikTokMediaType),
  hashtags: true,
  caption: true,
};
